import React, {useEffect, useRef, useState} from "react"
import * as Yup from "yup"
import {useSelector} from "react-redux"
import {useForm} from "react-hook-form"
import {Card, CardBody, CardTitle, Col, Form, FormGroup, Input, Label, Row} from "reactstrap"
import Switch from "react-bootstrap-switch";

// components
import ActivityLangs from "./_langs";
import Schedules from "./schedule/Schedules";
import Separator from "../../../components/Separator";
import TranslateButton from "../../../components/buttons/TranslateButton";
import ImagePicker from "../../../components/imagePickerComponents/ImagePicker";
import ModalFormTitle from "../../../components/genericComponents/ModalFormTitle";
import SelectGeneric from "../../../components/select/selectGeneric";
import {LogHistoryModal} from "../../../components/logHistory/logHistoryModal";
import {GenericButton} from "../../../components/buttons/customButtons";
import {GenericTableButton} from "../../../components/buttons/buttonsActions";

// labels
import {TT_CLOSE, TT_HISTORY, TT_SAVE} from "../../../components/i18n/toolTips";
import {LB_CODE, INTERNATIONAL, REQUIRED, LB_ACTIVE} from "../../../components/i18n/i18n"
import {PH_CAPACITY, PH_CODE, PH_HORARIO_DURATION, PH_ICON, PH_MINIMUN_STAY_REQUIRED,} from "../../../components/i18n/placeHolders"
import {FL_CAPACITY, FL_CATEGORY, FL_HORARIO_DURATION, FL_ICON, FL_MINIMUN_STAY_REQUIRED, FL_PLACE} from "../../../components/i18n/formLabels";

// services
import {hotelActivityService} from "../../../jwt/_services/hotel/hotelActivityService"
import {hotelActivityMediaService} from "../../../jwt/_services/core/hotelActivityMediaService";

// utils
import {getStateByPath} from "../../../redux/selectors";
import {getCurrentDateForCodeField} from "../../../util/DateTools";

// actions
import {genericIndexedActions} from "../../../redux/actions/generics/GenericIndexedActions";

// providers
import {placeProvider} from "../../../providers/placeProvider";
import {hotelCategoryProvider} from "../../../providers/hotelCategoryProvider";
import {widgetService} from "../../../jwt/_services/landings/widgetService";


const ActivityForm = props => {
    const componentState = useSelector(state => getStateByPath(state, props.path));
    const { path } = componentState;
    const { udn, app, userIsAdmin, editMode, domainObject} = componentState?.data;

    const {fbDataBase} = app
    const translatableLangs = domainObject?.translatedLangs || []

    const [categoryId, setCategoryId] = useState(domainObject?.category?.id != null ? domainObject?.category?.id : null);
    const [placeId, setPlaceId] = useState(domainObject?.place?.id != null ? domainObject?.place?.id : null);
    const [status, setStatus] = useState(domainObject?.status || false);
    const [langs, setLangs] = useState(domainObject?.langs || []);
    const [showModal, setShowModal] = useState(true)

    const modalRef = useRef(null);

    // Actualiza el formulario con los datos del objeto de dominio
    // Se utiliza posterior a la carga de imagenes desde ImagePicker ejecutando el metodo refresh()
    useEffect(() => {
        if (domainObject && domainObject.id && domainObject.langs) {
            reset({
                id: domainObject?.id,
                uid: domainObject?.uid,
                code: domainObject?.code,
                icon: domainObject?.icon,
                capacity: domainObject?.capacity,
                duration: domainObject?.duration,
                minimumStayRequired: domainObject?.minimumStayRequired,
            })
            setLangs(domainObject.langs || []);
            setStatus(domainObject.status);
        }
    }, [ domainObject ]);

    const showHistory = () => {
        setShowModal(true);
        modalRef.current.showModal(domainObject);
    }

    const validationSchema = Yup.object().shape({
        code: Yup.string().when('userIsAdmin',{
            is: true,
            then: Yup.string().required( REQUIRED ),
            otherwise: Yup.string()
        }),
        userIsAdmin: Yup.boolean()
    });


    const { register, handleSubmit, reset, errors } = useForm({
        validationSchema: validationSchema,
        defaultValues: {
            id: domainObject?.id,
            uid: domainObject?.uid,
            code: (domainObject?.code || `${udn?.code}_act_` + getCurrentDateForCodeField()).toLowerCase(), // AUTOCODE
            icon: domainObject?.icon,
            capacity: domainObject?.capacity,
            duration: domainObject?.duration,
            minimumStayRequired: domainObject?.minimumStayRequired,
            userIsAdmin: userIsAdmin
        }
    });


    const onSubmit = (data, _) => {
        let dataRequest = {
            fbDataBase,
            item : {
                ...data,
                status,
                udn : { id: udn?.id },
                place: (placeId != null) ? { id : placeId } : null,
                category: (categoryId != null) ? { id : categoryId } : null,
                langs
            }
        };

        if (!editMode)
            genericIndexedActions.save(path, hotelActivityService, dataRequest)
        else {
            let id = domainObject.id;
            dataRequest.item.id = id

            genericIndexedActions.update(path, hotelActivityService, id, dataRequest)
        }

        closeForm()
    };

    const closeForm = () => {
        reset();
        props.onClose()
    }

    const refreshObject = () => {
        genericIndexedActions.get(path, hotelActivityService, domainObject?.id, fbDataBase.id);
    }

    return <Form onSubmit={ handleSubmit(onSubmit) }>
        <Card className={'card-modal'}>
            <CardTitle className="bg-light border-bottom p-3 mb-0">
                <ModalFormTitle
                    domainObject={domainObject}
                    editMode={editMode}
                    udn={udn}
                />
                <div className={'top-right'}>
                    <GenericButton
                        style={'table'}
                        icon={'fa-save'}
                        toolTip={TT_SAVE}
                        permission={'add'}
                        type={'submit'}
                    />
                    {
                        editMode && <>
                            <GenericTableButton
                                toolTip={TT_HISTORY}
                                icon={'fa-clock'}
                                permission={'search'}
                                onClick={showHistory}
                            />
                            <TranslateButton item={domainObject} service={hotelActivityService} path={path}/>
                        </>
                    }
                    <Separator/>
                    <GenericButton
                        style={'danger-secondary'}
                        toolTip={TT_CLOSE}
                        icon={'fa-times'}
                        permission={'search'}
                        onClick={closeForm}
                    />
                </div>
            </CardTitle>
            <CardBody className={'card-body-modal'}>
                <Row>
                    <Col>
                        <Row>
                            <Col md="12">
                                <Row>
                                    {
                                        userIsAdmin ? <Col md="4">
                                            <FormGroup>
                                                <Label>{LB_CODE}</Label>
                                                <Input
                                                    type="text"
                                                    name="code"
                                                    placeholder={PH_CODE}
                                                    innerRef={register}
                                                />
                                            </FormGroup>
                                        </Col> : <Input
                                            type="hidden"
                                            name="code"
                                            placeholder={PH_CODE}
                                            innerRef={register}
                                        />
                                    }
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>{FL_ICON}</Label>
                                            <Input
                                                type="text"
                                                name="icon"
                                                placeholder={PH_ICON}
                                                innerRef={register}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3">
                                        <FormGroup>
                                            <Label>{LB_ACTIVE}</Label>
                                            <br/>
                                            <Switch
                                                animate={true}
                                                offText={"No"}
                                                onText={"Si"}
                                                value={status}
                                                onChange={() => setStatus(!status)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>{FL_CAPACITY}</Label>
                                            <Input
                                                type="text"
                                                name="capacity"
                                                placeholder={PH_CAPACITY}
                                                innerRef={register}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>{FL_HORARIO_DURATION}</Label>
                                            <Input
                                                type="text"
                                                name="duration"
                                                placeholder={PH_HORARIO_DURATION}
                                                innerRef={register}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="4">
                                        <FormGroup>
                                            <Label>{FL_MINIMUN_STAY_REQUIRED}</Label>
                                            <Input
                                                type="number"
                                                name="minimumStayRequired"
                                                min={0}
                                                placeholder={PH_MINIMUN_STAY_REQUIRED}
                                                innerRef={register}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{FL_PLACE}</Label>
                                            <SelectGeneric
                                                initialValue={ placeId }
                                                setExternalValue = { value => setPlaceId( value ) }
                                                isDisabled = { false }
                                                provider = { placeProvider }
                                                customFilter={{ item : {udn: {id: udn.id}}} }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label>{FL_CATEGORY}</Label>
                                            <SelectGeneric
                                                initialValue={ categoryId }
                                                setExternalValue = { value => setCategoryId( value ) }
                                                isDisabled = { false }
                                                provider = { hotelCategoryProvider }
                                                getAllCategories = { true }
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <div className={'form-container'}>
                            <div className={''}>
                                <h2>{INTERNATIONAL}</h2>
                            </div>
                            <div className={''}>
                                <Row>
                                    <ActivityLangs
                                        langs={langs}
                                        setLangs={setLangs}
                                        translatableLangs={translatableLangs}
                                    />
                                </Row>
                            </div>
                        </div>
                    </Col>
                    <Col>
                        <div className={'form-container'}>
                            <div className={''}>
                                <h2>{'Imagenes'}</h2>
                            </div>
                            <div className={''}>
                                <Row>
                                    <ImagePicker
                                        className={'hotelActivity'}
                                        title={''}
                                        app={app}
                                        service={hotelActivityService}
                                        baseMediaService={hotelActivityMediaService}
                                        objectMedia={domainObject}
                                        afterUpload={refreshObject}
                                    />
                                </Row>
                            </div>
                        </div>
                        <div className={'form-container'}>
                            <div className={''}>
                                <h2>{'Horarios'}</h2>
                            </div>
                            <div className={''}>
                                <Row>
                                    <Col>
                                        <Schedules
                                            items={domainObject?.schedules}
                                            path={path}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </Col>
                </Row>

            </CardBody>
            {
                showModal && <LogHistoryModal
                    ref={modalRef}
                    item={domainObject}
                />
            }
        </Card>
    </Form>
}
export default ActivityForm